import { Link } from 'react-router-dom';

import { Tag } from '../../../common/components/detail/detail';
import {
    IconHourglass, IconPause, IconPlay, IconReviews, IconSignal, IconTasks, IconUnknown,
} from '../../../common/icons';
import { Gate } from '../../../global/contexts/auth/auth';
import { ACTIVITY, criteriaProgress, readAgentActivity, readSignals } from '../utils/activity';
import { DESIRED_LABEL, PRESENCE_LABEL, VERB_LABEL, label } from '../utils/format';
import { ORDER_ACTIONS } from '../utils/operator';
import { Fact, Facts, Moment, RawJson, TechnicalDetails, Unknown } from './bits';

// La tarjeta de un agente, leída en el orden en que se pregunta:
//
//   1. qué está haciendo, y desde cuándo;
//   2. sobre qué tarea;
//   3. cuándo fue la última señal suya que quedó registrada;
//   4. si la cola le va a dar trabajo nuevo.
//
// LO QUE BAJÓ A LA AMPLIACIÓN: deseado, recibido y confirmado. Son tres etapas
// de UNA ORDEN —sirven para diagnosticar una autorización que no llegó— y
// ocupaban el lugar de las preguntas de todos los días. Siguen enteras, a un
// clic, con su explicación.
//
// UN REVISOR NO SALE «SIN TAREA». No toma tareas: lo suyo es una ronda
// reservada, que se lee de la bandeja de atención (utils/activity.js). Y
// cuando no hay ninguna publicada, la tarjeta dice que no se sabe —no que esté
// libre—, porque la bandeja deja de listar una ronda en cuanto se puede
// decidir, aunque el revisor siga en ella.

const ACTIVITY_ICON = {
    [ACTIVITY.running]:         <IconPlay />,
    [ACTIVITY.runningStale]:    <IconHourglass />,
    [ACTIVITY.reserved]:        <IconReviews />,
    [ACTIVITY.reservedExpired]: <IconHourglass />,
    [ACTIVITY.reviewerUnknown]: <IconUnknown />,
    [ACTIVITY.idle]:            <IconPause />,
};

export default function AgentRow({ row, review, observedAt, ordersEnabled, repositoryName, onAct }) {
    const { seat, presence, control, work, order, staleness } = row;
    const activity = readAgentActivity(row, review);
    const signals = readSignals(row);
    const criteria = criteriaProgress(activity);
    const paused = control.desired_state !== 'habilitado';
    const primary =
        control.desired_state === 'habilitado' && control.settled
            ? ORDER_ACTIONS.pausar_tomas
            : ORDER_ACTIONS.habilitar;

    return (
        <li className="coord-seat coord-agent">
            <header className="coord-seat__head">
                <div>
                    <strong className="coord-seat__name">
                        {seat.role === 'agent_master' ? 'Revisor' : 'Desarrollador'}
                    </strong>
                    <span className="coord-seat__id">
                        {repositoryName} ·{' '}
                        {seat.provider === 'claude_code'
                            ? 'Claude Code'
                            : seat.provider === 'codex'
                              ? 'Codex'
                              : seat.provider}
                    </span>
                </div>
                <div className="coord-seat__tags">
                    <Tag tone={presence.state === 'activa' ? 'active' : 'warn'}>
                        {label(PRESENCE_LABEL, presence.state)}
                    </Tag>
                </div>
            </header>

            {/* 1. QUÉ ESTÁ HACIENDO. Una línea, con su ícono y su matiz. */}
            <div className={`agent-now agent-now--${activity.tone}`}>
                <span className="agent-now__icon" aria-hidden="true">
                    {ACTIVITY_ICON[activity.key] ?? <IconUnknown />}
                </span>
                <div className="agent-now__body">
                    <p className="agent-now__headline">
                        {activity.headline}
                        {activity.roundNumber != null && (
                            <span className="agent-now__round"> · ronda {activity.roundNumber}</span>
                        )}
                    </p>

                    {/* 2. SOBRE QUÉ TAREA. */}
                    {activity.taskId != null ? (
                        <p className="agent-now__task">
                            <Gate
                                domain="tasks:read"
                                fallback={<>#{activity.taskId} · {activity.taskTitle ?? 'sin título'}</>}
                            >
                                <Link
                                    to={
                                        activity.key === ACTIVITY.reserved
                                        || activity.key === ACTIVITY.reservedExpired
                                            ? `/admin/tasks/${activity.taskId}?tab=revisiones`
                                            : `/admin/tasks/${activity.taskId}`
                                    }
                                >
                                    #{activity.taskId} · {activity.taskTitle ?? 'sin título'}
                                </Link>
                            </Gate>
                        </p>
                    ) : (
                        <p className="agent-now__task agent-now__task--none">
                            {activity.unknown ? 'Ninguna que esta pantalla pueda ver.' : 'Ninguna.'}
                        </p>
                    )}

                    <p className="agent-now__hint">{activity.hint}</p>
                </div>
            </div>

            {/* 3 y 4. LAS HORAS Y LA COLA, separadas porque contestan cosas
                distintas: una es lo último que quedó escrito de este puesto y
                la otra es si la cola le va a dar trabajo nuevo. */}
            <dl className="agent-facts">
                <AgentFact icon={<IconTasks />} label={activity.sinceLabel ?? 'Última tarea tomada'}>
                    {activity.since ? (
                        <Moment at={activity.since} observedAt={observedAt} />
                    ) : (
                        <Unknown>sin dato</Unknown>
                    )}
                </AgentFact>

                <AgentFact icon={<IconSignal />} label="Último reporte registrado">
                    {signals.last ? (
                        <>
                            <Moment at={signals.last} observedAt={observedAt} />
                            {signals.source && (
                                <span className="agent-facts__note"> · {signals.source}</span>
                            )}
                        </>
                    ) : (
                        <Unknown>sin señales</Unknown>
                    )}
                </AgentFact>

                {activity.leaseUntil && (
                    <AgentFact icon={<IconHourglass />} label="Vigencia">
                        <Moment at={activity.leaseUntil} observedAt={observedAt} />
                    </AgentFact>
                )}

                <AgentFact icon={paused ? <IconPause /> : <IconPlay />} label="Nuevas tareas">
                    {paused ? 'pausadas' : 'permitidas'}
                    <span className="agent-facts__note">
                        {control.settled ? ' · confirmado' : ' · sin confirmar'}
                    </span>
                </AgentFact>
            </dl>

            {criteria && (
                <p className="agent-now__criteria">
                    Criterios dictaminados: <strong>{criteria.evaluated} de {criteria.total}</strong>.
                    No es un porcentaje de avance: los criterios no pesan igual y pueden publicarse
                    todos al final.
                </p>
            )}

            {/* La marca durable del EQUIPO, que no es la actividad del agente.
                Se dice con ese nombre para que no se lea como lo otro. */}
            {signals.machineSeen && (
                <p className="coord__panel-note">
                    Última comunicación del equipo:{' '}
                    <Moment at={signals.machineSeen} observedAt={observedAt} />
                </p>
            )}

            {seat.current === false && (
                <p className="coord__error">
                    El vínculo de este puesto ya no está vigente. Revisa su registro antes de
                    enviar órdenes.
                </p>
            )}

            {staleness?.stale && (
                <p className="coord__panel-note">
                    Parte de este estado necesita confirmación. Consulta el detalle antes de
                    actuar.
                </p>
            )}

            {ordersEnabled && seat.current !== false && (
                <div className="coord-seat__actions">
                    <Gate all={[primary.permission, ...(primary.also ?? [])]}>
                        <button
                            type="button"
                            className="coord__button coord__button--primary"
                            onClick={() => onAct(primary)}
                        >
                            {primary.key === 'habilitar' ? 'Autorizar' : 'Pausar'}
                        </button>
                    </Gate>
                    {primary.key === 'habilitar' && control.desired_state === 'habilitado' && (
                        <Gate domain="machines:admin">
                            <button type="button" className="coord__button" onClick={() => onAct(ORDER_ACTIONS.pausar_tomas)}>
                                Pausar
                            </button>
                        </Gate>
                    )}
                    {work.live && (
                        <Gate domain="machines:admin">
                            <button
                                type="button"
                                className="coord__button"
                                onClick={() => onAct(ORDER_ACTIONS.interrumpir)}
                            >
                                Solicitar interrupción
                            </button>
                        </Gate>
                    )}
                    {!work.live && work.task && (
                        <Gate domain="machines:admin">
                            <button
                                type="button"
                                className="coord__button"
                                onClick={() => onAct(ORDER_ACTIONS.reintentar)}
                            >
                                Solicitar reintento
                            </button>
                        </Gate>
                    )}
                </div>
            )}

            <TechnicalDetails label="Ver estado y confirmaciones">
                <p className="coord__panel-note">
                    Las tres etapas de una orden. Sirven para diagnosticar una autorización que no
                    llegó; no dicen si el agente está trabajando.
                </p>
                <Stages control={control} observedAt={observedAt} />
                {order && <Order order={order} observedAt={observedAt} />}
                <RawJson value={row} label="Ver diagnóstico del puesto" />
            </TechnicalDetails>
        </li>
    );
}

function AgentFact({ icon, label: text, children }) {
    return (
        <div className="agent-facts__item">
            <dt>
                <span className="agent-facts__icon" aria-hidden="true">{icon}</span>
                {text}
            </dt>
            <dd>{children}</dd>
        </div>
    );
}

function Stages({ control, observedAt }) {
    const stages = [
        ['Deseado', control.requested, 'Lo que decidió una persona. Es lo que la cola obedece.'],
        ['Recibido', control.received, 'La orden LLEGÓ al equipo. No que se aplicara.'],
        [
            'Confirmado',
            control.confirmed,
            'Lo que el equipo dice que quedó. Solo un resultado ejecutado la mueve.',
        ],
    ];

    return (
        <ol className="coord-stages">
            {stages.map(([name, stage, note]) => (
                <li
                    key={name}
                    className={`coord-stages__step${stage ? ' coord-stages__step--on' : ''}`}
                >
                    <span className="coord-stages__name">{name}</span>
                    {stage ? (
                        <span className="coord-stages__value">
                            {label(DESIRED_LABEL, stage.state)} · rev. {stage.revision}
                            {stage.at && (
                                <>
                                    {' '}
                                    · <Moment at={stage.at} observedAt={observedAt} />
                                </>
                            )}
                        </span>
                    ) : (
                        <Unknown>todavía no</Unknown>
                    )}
                    <span className="coord-stages__note">{note}</span>
                </li>
            ))}
            <li className="coord-stages__summary">
                Efectivo: <strong>{label(DESIRED_LABEL, control.effective_state)}</strong>
                {' · '}rev. {control.revision}
                {' · '}
                {control.acknowledged ? 'acusada' : 'sin acuse'}
                {' · '}
                {control.executed ? 'ejecutada' : 'sin ejecutar'}
                {control.confirmation_superseded && ' · la confirmación quedó vieja'}
            </li>
        </ol>
    );
}

function Order({ order, observedAt }) {
    return (
        <section className="coord-seat__order">
            <h4>Orden vigente</h4>
            <Facts>
                <Fact label="Verbo">{label(VERB_LABEL, order.verb)}</Fact>
                <Fact label="Estado">{order.state}</Fact>
                <Fact label="Vale hasta">
                    <Moment at={order.not_after} observedAt={observedAt} />
                </Fact>
                <Fact label="Entrega">
                    {order.delivery ? (
                        `${order.delivery.state} · ${order.delivery.attempts} intento(s)`
                    ) : (
                        <Unknown>sin entrega</Unknown>
                    )}
                </Fact>
                {order.delivery?.last_error && (
                    <Fact label="Último error" wide>
                        <span className="coord-seat__error">{order.delivery.last_error}</span>
                    </Fact>
                )}
                <Fact label="Acuse">
                    {order.acked_at ? (
                        <Moment at={order.acked_at} observedAt={observedAt} />
                    ) : (
                        <Unknown>sin acuse</Unknown>
                    )}
                </Fact>
            </Facts>
        </section>
    );
}
